import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Printer } from 'lucide-react'
import { API_BASE_URL } from '@/api/config'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CashReceipt } from './CashReceipt'
import { guestReceiptLines, voucherReceiptLines, type GuestReceipt } from './guestReceipt'
import { fetchGuestReceipt, fetchRefundReceipt, fetchVoucherReceipt } from './service'
import { usePrintCashClosing } from './usePrintCashClosing'
import { DIALOG, MAIN, PICKED, QUIET, TITLE } from './posStyles'
import { cn } from '@/lib/utils'

/** Receipts follow the restaurant's language, not the tablet's. */
function useReceiptFormat() {
  const { i18n } = useTranslation('posOrdering')
  return (receipt: GuestReceipt, options: { copy?: boolean; hospitality?: boolean }) =>
    guestReceiptLines(receipt, { t: i18n.getFixedT(receipt.language, 'posOrdering'), language: receipt.language, ...options })
}
/** Every sale and refund needs a receipt, so the bon printer issues one on its own right after the money moved. */
export function useGuestReceiptPrint(restaurantId: number) {
  const { t } = useTranslation('posOrdering')
  const printing = usePrintCashClosing(restaurantId)
  const format = useReceiptFormat()
  const print = (orderIds: number[], refundId?: string) => {
    if (!printing.canPrint) return
    void (refundId ? fetchRefundReceipt(restaurantId, orderIds[0], refundId) : fetchGuestReceipt(restaurantId, orderIds)).then(receipt => printing.print(format(receipt, {}), receipt.language, { job: 'receipt', logo: true }))
      .catch(() => { toast.error(t('receipt.printFailed')) })
  }
  return Object.assign(print, { available: printing.canPrint })
}
export function GuestReceiptDialog({ restaurantId, orderIds, refundId, copy, onClose }: { restaurantId: number; orderIds: number[]; refundId?: string; copy: boolean; onClose: () => void }) {
  const { t } = useTranslation('posOrdering')
  const printing = usePrintCashClosing(restaurantId)
  const format = useReceiptFormat()
  const [hospitality, setHospitality] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)
  const receipt = useQuery({ queryKey: ['pos-receipt', restaurantId, orderIds.join(','), refundId],
    queryFn: ({ signal }) => refundId ? fetchRefundReceipt(restaurantId, orderIds[0], refundId, signal) : fetchGuestReceipt(restaurantId, orderIds, signal), retry: false })
  const data = receipt.data
  const lines = data && format(data, { copy, hospitality })
  return <Dialog open onOpenChange={open => { if (!open) onClose() }}>
    <DialogContent className={cn(DIALOG, 'flex max-h-[92dvh] w-[calc(100vw-1rem)] max-w-md flex-col')}>
      <DialogHeader className="text-start">
        <DialogTitle className={TITLE}>{t('receipt.title')}</DialogTitle>
        <DialogDescription className="sr-only">{t('receipt.number', { number: orderIds.join(', ') })}</DialogDescription>
      </DialogHeader>
      <div className="flex min-h-0 flex-1 justify-center overflow-y-auto py-1">
        {receipt.isPending && <p role="status">{t('receipt.loading')}</p>}
        {receipt.isError && <p role="alert" className="text-sm text-destructive">{t('receipt.unavailable')}</p>}
        {lines && <CashReceipt lines={lines} header={!logoFailed && <img src={`${API_BASE_URL}/restaurantIcon/${restaurantId}`} alt="" onError={() => setLogoFailed(true)} className="mx-auto mb-2 max-h-14 max-w-[60%] object-contain" />} />}
      </div>
      {lines && <div className="flex flex-wrap justify-end gap-2">
        {!refundId && <Button type="button" variant="ghost" aria-pressed={hospitality} className={cn(QUIET, 'h-12 px-4', hospitality && PICKED)} onClick={() => setHospitality(!hospitality)}>{t('receipt.hospitality')}</Button>}
        {printing.canPrint && <Button type="button" disabled={printing.isPrinting} className={cn(MAIN, 'h-12 px-4')}
          onClick={() => { void printing.print(lines, data.language, { job: 'receipt', logo: true }).catch(() => { toast.error(t('receipt.printFailed')) }) }}><Printer aria-hidden />{t('receipt.print')}</Button>}
      </div>}
    </DialogContent>
  </Dialog>
}
/** The sold voucher, with the code the guest redeems it by. */
export function VoucherReceiptDialog({ restaurantId, id, copy, autoPrint = false, onClose }: { restaurantId: number; id: string; copy: boolean; autoPrint?: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation('posOrdering')
  const printing = usePrintCashClosing(restaurantId)
  const receipt = useQuery({ queryKey: ['pos-voucher-receipt', restaurantId, id], queryFn: ({ signal }) => fetchVoucherReceipt(restaurantId, id, signal), retry: false })
  const data = receipt.data
  const lines = data && voucherReceiptLines(data, { t: i18n.getFixedT(data.language, 'posOrdering'), language: data.language, copy })
  const printed = useRef(false)
  useEffect(() => {
    if (!autoPrint || printed.current || !lines || !printing.canPrint) return
    printed.current = true
    void printing.print(lines, data.language, { job: 'receipt', logo: true }).catch(() => { toast.error(t('receipt.printFailed')) })
  })
  return <Dialog open onOpenChange={open => { if (!open) onClose() }}>
    <DialogContent className={cn(DIALOG, 'flex max-h-[92dvh] w-[calc(100vw-1rem)] max-w-md flex-col')}>
      <DialogHeader className="text-start">
        <DialogTitle className={TITLE}>{t('voucher.title')}</DialogTitle>
        <DialogDescription className="sr-only">{t('voucher.title')}</DialogDescription>
      </DialogHeader>
      <div className="flex min-h-0 flex-1 justify-center overflow-y-auto py-1">
        {receipt.isPending && <p role="status">{t('receipt.loading')}</p>}
        {receipt.isError && <p role="alert" className="text-sm text-destructive">{t('receipt.unavailable')}</p>}
        {lines && <CashReceipt lines={lines} />}
      </div>
      {lines && printing.canPrint && <div className="flex justify-end"><Button type="button" disabled={printing.isPrinting} className={cn(MAIN, 'h-12 px-4')}
        onClick={() => { void printing.print(lines, data.language, { job: 'receipt', logo: true }).catch(() => { toast.error(t('receipt.printFailed')) }) }}><Printer aria-hidden />{t('receipt.print')}</Button></div>}
    </DialogContent>
  </Dialog>
}
